import { activeLang, useT } from '../lib/i18n';
import { translate } from '@bingo/shared';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import type { CodeCountDto, Import2026PreviewDto, Import2026StatusDto } from '@bingo/shared';
import { api, ApiError } from '../lib/api';

type Step = 'pick' | 'sending' | 'preview' | 'running' | 'done';

/** Sends the file with a progress bar (fetch cannot report upload progress). */
function sendFile(file: File, onProgress: (pct: number) => void): Promise<Import2026PreviewDto> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', '/api/owner/import-2026/upload');
    xhr.withCredentials = true;
    xhr.setRequestHeader('content-type', 'application/octet-stream');
    xhr.setRequestHeader('x-lang', activeLang);
    xhr.upload.onprogress = (e) => e.lengthComputable && onProgress(Math.round((e.loaded / e.total) * 100));
    xhr.onload = () => {
      let data: { error?: string; message?: string } | null = null;
      try {
        data = JSON.parse(xhr.responseText);
      } catch {
        data = null;
      }
      if (xhr.status === 200) resolve(data as unknown as Import2026PreviewDto);
      else reject(new ApiError(xhr.status, data?.error ?? 'error', data?.message ?? translate(activeLang, 'Upload failed ({n}).', { n: xhr.status })));
    };
    xhr.onerror = () => reject(new ApiError(0, 'offline', translate(activeLang, 'No connection to the server. Check the internet and try again.')));
    xhr.send(file);
  });
}

function Codes({ title, list }: { title: string; list: CodeCountDto[] }) {
  const t = useT();
  if (!list.length) return null;
  return (
    <div>
      <b>{t(title)}</b>
      <div className="code-list">
        {list.map((c) => <span key={c.code} className="badge badge-accent">{c.code} · {c.count}</span>)}
      </div>
    </div>
  );
}

/** Master only: brings the bookings of Bingo App 2026 into this app. */
export function ImportHistory() {
  const [step, setStep] = useState<Step>('pick');
  const [pct, setPct] = useState(0);
  const [preview, setPreview] = useState<Import2026PreviewDto | null>(null);
  const [status, setStatus] = useState<Import2026StatusDto | null>(null);
  const [error, setError] = useState('');
  const input = useRef<HTMLInputElement>(null);
  const t = useT();

  useEffect(() => {
    if (step !== 'running') return;
    const timer = setInterval(async () => {
      try {
        const s = await api<Import2026StatusDto>('/api/owner/import-2026/status');
        setStatus(s);
        if (s.state !== 'running') setStep('done');
      } catch {
        /* keep polling */
      }
    }, 1500);
    return () => clearInterval(timer);
  }, [step]);

  async function choose(file: File | undefined) {
    if (!file) return;
    setError('');
    setPct(0);
    setStep('sending');
    try {
      setPreview(await sendFile(file, setPct));
      setStep('preview');
    } catch (e) {
      setError(e instanceof ApiError ? e.message : t('The file could not be sent.'));
      setStep('pick');
    } finally {
      if (input.current) input.current.value = '';
    }
  }

  async function start() {
    if (!preview) return;
    setError('');
    try {
      setStatus(await api<Import2026StatusDto>('/api/owner/import-2026/apply', { method: 'POST', body: { uploadId: preview.uploadId } }));
      setStep('running');
    } catch (e) {
      setError(e instanceof ApiError ? e.message : t('The import could not start.'));
    }
  }

  async function cancel() {
    await api('/api/owner/import-2026', { method: 'DELETE' }).catch(() => undefined);
    setPreview(null);
    setStep('pick');
  }

  const p = preview;
  const total = (status?.total ?? 0) || 1;

  return (
    <div className="stack" style={{ maxWidth: 720 }}>
      <div className="page-head"><h1>{t('Import history from Bingo App 2026')}</h1></div>
      {error && <div className="alert alert-bad">{error}</div>}

      {(step === 'pick' || step === 'sending') && (
        <div className="card section">
          <span>{t('Choose the backup file bingo-YYYYMMDD.db. A big file is faster to send compressed: right click on it, Send to › Compressed (zipped) folder, then choose the .zip here.')}</span>
          <span className="muted" style={{ fontSize: 13 }}>{t('The file goes straight to your server and is deleted after the import.')}</span>
          <span className="muted" style={{ fontSize: 13 }}>{t('Nothing is imported yet: you first see the numbers and confirm.')}</span>
          {step === 'sending' ? (
            <div className="stack" style={{ gap: 6 }}>
              <div className="progress"><div style={{ width: `${pct}%` }} /></div>
              <span className="muted" style={{ fontSize: 13 }}>{pct < 100 ? t('Sending… {n}%', { n: pct }) : t('Reading the file…')}</span>
            </div>
          ) : (
            <div>
              <input ref={input} type="file" accept=".db,.sqlite,.gz,.zip,application/octet-stream,application/zip" hidden onChange={(e) => choose(e.target.files?.[0])} />
              <button className="btn btn-primary" onClick={() => input.current?.click()}>{t('Choose the file')}</button>
            </div>
          )}
        </div>
      )}

      {step === 'preview' && p && (
        <>
          <div className="card section">
            <h2>{t('What the file contains')}</h2>
            <table className="table">
              <thead><tr><th>{t('Year')}</th><th>{t('Bookings')}</th><th>{t('Cancelled (X)')}</th></tr></thead>
              <tbody>
                {p.years.map((y) => <tr key={y.year}><td><b>{y.year}</b></td><td>{y.bookings}</td><td>{y.cancelled}</td></tr>)}
              </tbody>
            </table>
            <div className="import-nums">
              <div><b>{p.willCreate}</b><span>{t('new bookings')}</span></div>
              <div><b>{p.willUpdate}</b><span>{t('already here (refreshed, your edits kept)')}</span></div>
              <div><b>{p.duplicatesInFile}</b><span>{t('duplicates in the file (newest kept)')}</span></div>
              <div><b>{p.skipped}</b><span>{t('skipped (no valid date)')}</span></div>
            </div>
            <span className="muted" style={{ fontSize: 13 }}>
              {t('Hotels recognised from the list: {a} of {b}. The others keep the hotel as written.', { a: p.hotelsMatched, b: p.withHotel })}
              {p.products > 0 && ` ${t('The platform product list ({n} lines) is copied too: it tells which tour each Bokun or Bookingkit product is.', { n: p.products })}`}
            </span>
          </div>

          {p.transfers > 0 && (
            <div className="card section">
              <h2>{t('Transfers')}</h2>
              <div className="import-nums">
                <div><b>{p.transfersWillCreate}</b><span>{t('new transfers')}</span></div>
                <div><b>{p.transfersWillUpdate}</b><span>{t('already here (only empty fields filled)')}</span></div>
                <div><b>{p.transfersFuture}</b><span>{t('from today on, not cancelled')}</span></div>
                <div><b>{p.transfersSkipped}</b><span>{t('skipped (no valid date)')}</span></div>
              </div>
              {p.transfersGeneric > 0 && (
                <span className="muted" style={{ fontSize: 13 }}>
                  {t('{n} transfers have a route that is not a product: they are imported as "TR" with their From and To places as written.', { n: p.transfersGeneric })}
                </span>
              )}
            </div>
          )}

          {(p.unknownTours.length > 0 || p.unknownChannels.length > 0 || p.unknownLanguages.length > 0 || p.problems.noPax > 0 || p.problems.tooManyPax > 0) && (
            <div className="card section">
              <h2>{t('To check after the import')}</h2>
              <span className="muted" style={{ fontSize: 13 }}>{t('These bookings are imported anyway. Codes not found in Lists stay empty; add them in Lists and send the file again to fill them.')}</span>
              <Codes title="Tour codes not in Lists" list={p.unknownTours} />
              <Codes title="Channel codes not in Lists" list={p.unknownChannels} />
              <Codes title="Unusual languages" list={p.unknownLanguages} />
              {p.problems.noPax > 0 && <span>{t('{n} bookings with 0 passengers.', { n: p.problems.noPax })}</span>}
              {p.problems.tooManyPax > 0 && <span>{t('{n} bookings above 99 passengers (set to 99).', { n: p.problems.tooManyPax })}</span>}
            </div>
          )}

          <div className="row-actions" style={{ justifyContent: 'flex-start' }}>
            <button className="btn btn-primary" onClick={start}>{t('Import {n} bookings', { n: p.willCreate + p.willUpdate + p.transfersWillCreate + p.transfersWillUpdate })}</button>
            <button className="btn" onClick={cancel}>{t('Cancel')}</button>
          </div>
        </>
      )}

      {(step === 'running' || step === 'done') && status && (
        <div className="card section">
          <h2>{status.state === 'running' ? t('Importing…') : status.state === 'done' ? t('Import finished') : t('Import stopped')}</h2>
          <div className="progress"><div style={{ width: `${Math.round((status.done / total) * 100)}%` }} /></div>
          <span className="muted" style={{ fontSize: 13 }}>{t('{a} of {b}. You can leave this page; the import continues.', { a: status.done, b: status.total })}</span>
          {status.state !== 'running' && (
            <div className="import-nums">
              <div><b>{status.created}</b><span>{t('created')}</span></div>
              <div><b>{status.updated}</b><span>{t('refreshed')}</span></div>
              <div><b>{status.unchanged}</b><span>{t('unchanged')}</span></div>
              <div><b>{status.keptTeamEdits}</b><span>{t('team edits kept')}</span></div>
            </div>
          )}
          {status.error && <div className="alert alert-bad">{status.error}</div>}
          {status.state !== 'running' && (
            <div className="row-actions" style={{ justifyContent: 'flex-start' }}>
              <Link className="btn btn-primary" to="/calendar">{t('Open the calendar')}</Link>
              <button className="btn" onClick={() => { setStatus(null); setPreview(null); setStep('pick'); }}>{t('Send another file')}</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
